import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { getApiBase } from '../utils/apiConfig';
import { useNavigate } from 'react-router-dom';
import EscrowStripePayment from '../components/EscrowStripePayment';
import ProgressStepper from '../components/ProgressStepper.jsx';
import MovdInput from '../design-system/MovdInput.jsx';
import MovdButton from '../design-system/MovdButton.jsx';
import MovdConfirmation from '../design-system/MovdConfirmation.jsx';
import { movdTokens } from '../design-system/movdTokens.js';

// Explicit booking flow states — never treat 'booking created' as 'confirmed'.
const FLOW = { REVIEW: 'review', PAYMENT: 'payment', PROCESSING: 'processing', CONFIRMED: 'confirmed', FAILED: 'failed' };
const formatMoveDate = value => {
  if (!value) return 'N/A';
  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(value);
  const date = new Date(isDateOnly ? `${value}T12:00:00` : value);
  if (Number.isNaN(date.getTime())) return 'N/A';
  return date.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    ...(isDateOnly ? {} : { hour: 'numeric', minute: '2-digit', hour12: true }),
  });
};

const cardStyle = { borderRadius: 16, padding: 'clamp(20px, 4vw, 36px)', marginBottom: 24, background: movdTokens.color.surface.base, boxShadow: movdTokens.shadow.panel };
const labelStyle = { color: movdTokens.color.action.primary, fontWeight: 900, letterSpacing: '.1em', fontSize: 12, textTransform: 'uppercase', margin: 0 };
const mutedStyle = { color: movdTokens.color.content.secondary, fontSize: 14, margin: '4px 0 0' };
const alertStyle = tone => ({
  borderRadius: 10,
  padding: '12px 16px',
  marginTop: 16,
  fontSize: 14,
  fontWeight: 600,
  background: tone === 'error' ? movdTokens.color.state.errorSurface : tone === 'success' ? movdTokens.color.state.successSurface : movdTokens.color.state.disabledSurface,
  color: tone === 'error' ? movdTokens.color.state.error : tone === 'success' ? movdTokens.color.state.success : movdTokens.color.content.secondary,
});

export default function Booking() {
  // Defensive: check for required data
  const selectedQuote = JSON.parse(sessionStorage.getItem('selectedQuote') || '{}');
  const moveDetails = JSON.parse(sessionStorage.getItem('moveDetails') || '{}');
  const inventory = JSON.parse(sessionStorage.getItem('inventory') || '[]');
  const userId = sessionStorage.getItem('userId') || 'demo-user-id';
  const navigate = useNavigate();
  const hasRequiredData = Boolean(selectedQuote?.price && moveDetails?.origin && moveDetails?.destination);

  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [emailError, setEmailError] = useState(''); // New state for email validation
  const [showPayment, setShowPayment] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [bookingId, setBookingId] = useState(null); // Store booking ID for escrow payment
  const [checkoutToken, setCheckoutToken] = useState(() => sessionStorage.getItem('bookingCheckoutToken') || '');

  // --- V2 state ---
  const [flow, setFlow] = useState(FLOW.REVIEW);
  const [bookingData, setBookingData] = useState(null);      // authoritative booking from server
  const [priceChanged, setPriceChanged] = useState(null);    // { oldPrice, newPrice } when server recalculates
  const [finalizing, setFinalizing] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [copied, setCopied] = useState(false);
  const [paymentIntent, setPaymentIntent] = useState(null);

  useEffect(() => {
    // Remove auto-redirect to /confirmation for three-step in-place flow
    // if (paymentSuccess) {
    //   navigate('/confirmation');
    // }
  }, [paymentSuccess, navigate]);

  useEffect(() => {
    if (hasRequiredData) return undefined;
    const redirectTimer = setTimeout(() => navigate('/get-quote'), 1000);
    return () => clearTimeout(redirectTimer);
  }, [hasRequiredData, navigate]);

  // Always compute priceNumber for use in payment and display
  const priceNumber = useMemo(() => (
    typeof selectedQuote.price === 'string'
      ? Number(String(selectedQuote.price).replace(/[^\d.]/g, ''))
      : selectedQuote.price
  ), [selectedQuote.price]);

  // Keep the recovery message rendered briefly so the customer understands why
  // they are being returned to the quote flow.
  if (!hasRequiredData) {
    return (
      <main id="main-content" role="main" style={{ padding: 32, minHeight: '100vh', background: movdTokens.color.background.canvas }}>
        <div role="status" style={alertStyle('error')}>Required booking information is missing. Redirecting to start...</div>
      </main>
    );
  }

  const handleBook = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);
    setEmailError('');
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(contactEmail)) {
      setEmailError('Please enter a valid email address.');
      setLoading(false);
      return;
    }
    try {
      // Move these above the log so the log is accurate
      const safeUserId = userId === 'demo-user-id' ? 'demo-user-id' : userId;
      const safeMoverId = (selectedQuote.moverId || selectedQuote.mover?._id) === 'demo-mover-id'
        ? 'demo-mover-id'
        : (selectedQuote.moverId || selectedQuote.mover?._id);
      // Ensure inventory is an array of IDs (duplicated for quantity)
      let inventoryIds = [];
      const inventoryDetails = Array.isArray(inventory) ? inventory : [];
      const isMongoId = value => typeof value === 'string' && /^[a-fA-F0-9]{24}$/.test(value);
      if (Array.isArray(inventory) && inventory.length > 0) {
        if (typeof inventory[0] === 'string') {
          // Inventory is array of IDs, possibly repeated for quantity
          inventoryIds = inventory.filter(isMongoId);
        } else if (typeof inventory[0] === 'object') {
          // Keep only persisted IDs in the legacy reference field. Custom
          // items remain in inventoryDetails for server-side pricing.
          inventory.forEach(item => {
            const id = item.id || item._id || item;
            const qty = item.qty ?? item.quantity ?? 1;
            if (isMongoId(id)) {
              for (let i = 0; i < qty; i++) inventoryIds.push(id);
            }
          });
        }
      }
      const bookingIdempotencyKey = sessionStorage.getItem('bookingIdempotencyKey') || `booking-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      sessionStorage.setItem('bookingIdempotencyKey', bookingIdempotencyKey);
      // Submit booking to backend. The server recalculates non-demo prices
      // through the pricing engine — the returned booking price is authoritative.
      const bookingRes = await axios.post(`${getApiBase()}/bookings`, {
        user: safeUserId,
        mover: safeMoverId,
        quoteId: selectedQuote.quoteId,
        idempotencyKey: bookingIdempotencyKey,
        inventory: inventoryIds,
        inventoryDetails,
        origin: moveDetails.origin,
        destination: moveDetails.destination,
        moveDate: moveDetails.moveDate,
        price: priceNumber,
        distanceKm: selectedQuote.pricingSnapshot?.distanceKm || moveDetails.distanceKm,
        contactName,
        contactEmail,
        contactPhone,
        specialInstructions
      });

      const created = bookingRes.data || {};
      setBookingData(created);
      setBookingId(created._id || created.id);
      if (created.checkoutToken) {
        setCheckoutToken(created.checkoutToken);
        sessionStorage.setItem('bookingCheckoutToken', created.checkoutToken);
      }
      sessionStorage.setItem('bookingDetails', JSON.stringify(created));

      // Authoritative price check: if the server-calculated price differs from
      // the displayed quote, do NOT charge silently — make the customer review it.
      const serverPrice = Number(created.price);
      if (Number.isFinite(serverPrice) && Number.isFinite(priceNumber) && Math.abs(serverPrice - priceNumber) > 0.5) {
        setPriceChanged({ oldPrice: priceNumber, newPrice: serverPrice });
        setLoading(false);
        return; // stay on review state, ask customer to acknowledge the new price
      }

      setShowPayment(true);
      setFlow(FLOW.PAYMENT);
      setSuccess(true);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Booking/payment failed.');
      setFlow(FLOW.FAILED);
    } finally {
      setLoading(false);
    }
  };

  // Customer acknowledges the authoritative price changed — proceed to payment
  // at the new (server) amount.
  const handleAcknowledgePrice = () => {
    setPriceChanged(null);
    setShowPayment(true);
    setFlow(FLOW.PAYMENT);
  };

  // After Stripe confirms the payment, finalize the booking server-side so the
  // booking is marked confirmed and the payment recorded before we show success.
  const finalizeBooking = async (paymentIntent) => {
    if (!bookingId || !paymentIntent?.id) {
      setError('We could not verify your booking payment. Please try again or contact support.');
      setFlow(FLOW.FAILED);
      return;
    }
    setPaymentIntent(paymentIntent);
    setFinalizing(true);
    setFlow(FLOW.PROCESSING);
    try {
      const res = await axios.post(`${getApiBase()}/bookings/${bookingId}/payment-complete`, {
        paymentIntentId: paymentIntent?.id,
        ...(checkoutToken ? { checkoutToken } : {}),
      }, { timeout: 20000 });
      const updated = res.data?.booking || null;
      const paymentFinalized = res.data?.success === true
        && updated
        && ['pending', 'confirmed'].includes(updated.status);
      if (!paymentFinalized) {
        throw new Error('Booking payment finalization was not returned by the server.');
      }
      if (updated) {
        setBookingData(prev => ({ ...(prev || {}), ...updated }));
        sessionStorage.setItem('bookingDetails', JSON.stringify({ ...bookingData, ...updated }));
      }
      setEmailSent(res.data?.emailSent === true);
      setPaymentSuccess(true);
      setFlow(FLOW.CONFIRMED);
    } catch {
      // Payment succeeded but finalization failed — surface a recoverable state.
      setError('Your payment succeeded, but we could not finalize the booking. Please contact support with your booking reference.');
      setFlow(FLOW.FAILED);
    } finally {
      setFinalizing(false);
    }
  };

  const bookingReference = bookingData?.jobNumber || (bookingId ? `MOVD-${String(bookingId).slice(-6).toUpperCase()}` : null);
  const handleCopyReference = async () => {
    if (!bookingReference) return;
    try {
      await navigator.clipboard.writeText(bookingReference);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access is optional; the reference remains visible for manual copying.
    }
  };

  // --- Mover profile header ---
  const mover = selectedQuote.mover || {};
  const moverName = mover.name || mover.companyName || mover.displayName || 'Mover';
  const inventoryItemCount = Array.isArray(inventory)
    ? inventory.reduce((total, item) => total + (item.qty ?? item.quantity ?? 1), 0)
    : 0;
  const inventoryBoxCount = Array.isArray(inventory)
    ? inventory.filter(item => /box/i.test(item.name || '')).reduce((total, item) => total + (item.qty ?? item.quantity ?? 1), 0)
    : 0;
  const formatPrice = (price) => {
    if (typeof price === 'string' && price.startsWith('$')) return price;
    return `$${price}`;
  };

  if (paymentSuccess) {
    // Confirmation — prefer authoritative booking data from the server.
    const bd = bookingData || JSON.parse(sessionStorage.getItem('bookingDetails') || '{}');
    const confPrice = Number(bd.price ?? priceNumber);
    const paid = bd.paymentStatus !== 'failed';
    const awaitingMoverAcceptance = bd.status === 'pending' && bd.moverAcceptanceRequired !== false;
    return <MovdConfirmation
      move={{ ...moveDetails, inventory }}
      mover={mover}
      bookingReference={bookingReference}
      total={confPrice}
      paid={paid}
      awaitingMoverAcceptance={awaitingMoverAcceptance}
      emailSent={emailSent}
      email={contactEmail}
      copied={copied}
      onCopy={handleCopyReference}
      onDownload={async () => {
        try { await import('../components/BookingReceipt.jsx').then(module => module.downloadReceipt({ bookingRef: bookingReference, selectedMover: mover, serviceDetails: moveDetails, bookingDetails: { contactInfo: { email: contactEmail, phone: contactPhone } }, paymentInfo: { total: confPrice } })); } catch { /* Receipt action remains recoverable through the booking portal. */ }
      }}
      downloading={false}
      onViewBookings={() => navigate('/user-dashboard')}
      onReturnHome={() => navigate('/')}
    />;
  }

  // Authoritative price changed — require acknowledgement before payment.
  if (priceChanged) {
    return (
      <main id="main-content" role="main" style={{ background: movdTokens.color.background.canvas, minHeight: '100vh', padding: 32 }}>
        <div style={{ maxWidth: 480, margin: '0 auto' }}>
          <div style={{ ...cardStyle, textAlign: 'center' }}>
            <h1 style={{ margin: 0, fontSize: 24, fontWeight: 900, color: movdTokens.color.content.primary }}>Your quote has changed</h1>
            <p style={{ ...mutedStyle, marginBottom: 20 }}>We recalculated your price based on your move details. Please review the updated price before continuing.</p>
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'baseline', gap: 16, marginBottom: 24 }}>
              <span style={{ fontSize: 20, color: movdTokens.color.content.muted, textDecoration: 'line-through' }}>${Number(priceChanged.oldPrice).toLocaleString('en-CA')}</span>
              <span style={{ fontSize: 32, fontWeight: 900, color: movdTokens.color.action.primary }}>${Number(priceChanged.newPrice).toLocaleString('en-CA')}</span>
            </div>
            <div style={{ display: 'flex', gap: 12, flexDirection: 'column' }}>
              <MovdButton onClick={handleAcknowledgePrice}>Continue at ${Number(priceChanged.newPrice).toLocaleString('en-CA')}</MovdButton>
              <MovdButton variant="secondary" onClick={() => navigate('/results')}>Back to results</MovdButton>
            </div>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main id="main-content" role="main" style={{ background: movdTokens.color.background.canvas, color: movdTokens.color.content.primary, minHeight: '100vh', padding: 'clamp(16px, 4vw, 32px)' }}>
      <div style={{ marginBottom: 24 }}><ProgressStepper currentStep={3} /></div>
      <div style={{ maxWidth: 560, margin: '0 auto' }}>
        <div style={cardStyle}>
          <p style={labelStyle}>Review your move</p>
          <p style={mutedStyle}>Everything looks right? Continue when you&apos;re ready to secure your booking.</p>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, marginTop: 20, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 0 }}>
              <div style={{ width: 56, height: 56, borderRadius: '50%', background: movdTokens.color.inkSoft, color: movdTokens.color.teal, display: 'grid', placeItems: 'center', fontWeight: 900, fontSize: 24, flexShrink: 0, overflow: 'hidden' }}>
                {mover.logo || mover.profileImage ? <img src={mover.logo || mover.profileImage} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : moverName[0]}
              </div>
              <div style={{ minWidth: 0 }}>
                <h2 style={{ margin: 0, fontSize: 20, fontWeight: 900, color: movdTokens.color.action.primary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{moverName}</h2>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 6 }}>
                  {mover.rating && <span style={{ fontSize: 13, fontWeight: 700 }}>★ {Number(mover.rating).toFixed(1)}</span>}
                  {mover.reviewCount && <span style={{ fontSize: 12, color: movdTokens.color.content.secondary }}>({mover.reviewCount} reviews)</span>}
                  {mover.jobs && <span style={{ fontSize: 11, fontWeight: 700, background: movdTokens.color.state.disabledSurface, borderRadius: 5, padding: '3px 8px' }}>{mover.jobs} moves</span>}
                  {mover.yearsInBusiness && <span style={{ fontSize: 11, fontWeight: 700, background: movdTokens.color.state.disabledSurface, borderRadius: 5, padding: '3px 8px' }}>{mover.yearsInBusiness} yrs</span>}
                  {mover.insurance && <span style={{ fontSize: 11, fontWeight: 700, background: movdTokens.color.state.successSurface, color: movdTokens.color.state.success, borderRadius: 5, padding: '3px 8px' }}>Insured</span>}
                </div>
                {Array.isArray(mover.locations) && mover.locations.length > 0 && (
                  <p style={{ margin: '6px 0 0', fontSize: 12, color: movdTokens.color.content.secondary }}>
                    Serving: {mover.locations.slice(0, 3).join(', ')}{mover.locations.length > 3 ? ` +${mover.locations.length - 3} more` : ''}
                  </p>
                )}
              </div>
            </div>
            <div style={{ fontSize: 24, fontWeight: 900, color: movdTokens.color.action.primary, flexShrink: 0 }}>{formatPrice(selectedQuote.price)}</div>
          </div>

          <div style={{ height: 1, background: movdTokens.color.border.subtle, margin: '20px 0' }} />

          <div style={{ display: 'grid', gap: 10, background: movdTokens.color.background.canvas, borderRadius: 12, padding: 16 }}>
            <p style={{ ...labelStyle, fontSize: 11 }}>Move summary</p>
            <div style={{ display: 'flex', gap: 8 }}><strong style={{ minWidth: 90, fontSize: 14 }}>Origin:</strong><span style={{ fontSize: 14, color: movdTokens.color.content.secondary }}>{selectedQuote.origin || moveDetails.origin}</span></div>
            <div style={{ display: 'flex', gap: 8 }}><strong style={{ minWidth: 90, fontSize: 14 }}>Destination:</strong><span style={{ fontSize: 14, color: movdTokens.color.content.secondary }}>{selectedQuote.destination || moveDetails.destination}</span></div>
            <div style={{ display: 'flex', gap: 8 }}><strong style={{ minWidth: 90, fontSize: 14 }}>Date:</strong><span style={{ fontSize: 14, color: movdTokens.color.content.secondary }}>{formatMoveDate(selectedQuote.moveDate || moveDetails.moveDate)}</span></div>
            <div style={{ display: 'flex', gap: 8 }}><strong style={{ minWidth: 90, fontSize: 14 }}>Move size:</strong><span style={{ fontSize: 14, color: movdTokens.color.content.secondary }}>{selectedQuote.moveSize || moveDetails.moveSize || 'N/A'}</span></div>
          </div>

          {inventoryItemCount > 0 && (
            <div style={{ marginTop: 20, padding: 16, borderRadius: 10, background: movdTokens.color.surface.base, border: `1px solid ${movdTokens.color.border.subtle}` }}>
              <p style={{ margin: 0, fontWeight: 800, fontSize: 14 }}>Your inventory</p>
              <p style={{ ...mutedStyle }}>{inventoryItemCount} items{inventoryBoxCount ? ` · ${inventoryBoxCount} boxes` : ''}</p>
              {inventory.some(item => item.estimate) && <p style={{ margin: '4px 0 0', color: movdTokens.color.state.warning, fontSize: 12 }}>Based on an estimate</p>}
            </div>
          )}

          {!showPayment ? (
            <form noValidate onSubmit={handleBook} style={{ marginTop: 24, display: 'grid', gap: 16 }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>Contact & special instructions</h3>
              <MovdInput label="Full name" value={contactName} onChange={e => setContactName(e.target.value)} required aria-label="Full Name" />
              <MovdInput label="Email" type="email" value={contactEmail} onChange={e => setContactEmail(e.target.value)} required error={emailError} aria-label="Email" />
              <MovdInput label="Phone" value={contactPhone} onChange={e => setContactPhone(e.target.value)} required aria-label="Phone" />
              <MovdInput label="Special instructions" value={specialInstructions} onChange={e => setSpecialInstructions(e.target.value)} aria-label="Special Instructions" inputStyle={{ minHeight: 92, paddingTop: 14, paddingBottom: 14 }} />
              <MovdButton type="submit" loading={loading} style={{ width: '100%' }} aria-label="Book now">Continue to payment</MovdButton>
            </form>
          ) : (
            <div style={{ marginTop: 32 }}>
              {flow === FLOW.PROCESSING ? (
                <div style={{ textAlign: 'center', padding: '32px 0' }}>
                  <div aria-hidden="true" style={{ width: 32, height: 32, margin: '0 auto', borderRadius: '50%', border: `3px solid ${movdTokens.color.border.subtle}`, borderTopColor: movdTokens.color.action.primary, animation: 'movd-spin 0.8s linear infinite' }} />
                  <style>{'@keyframes movd-spin { to { transform: rotate(360deg); } }'}</style>
                  <p style={{ marginTop: 16, fontWeight: 700 }}>Processing your booking…</p>
                  <p style={mutedStyle}>Please don&apos;t close this page.</p>
                </div>
              ) : flow === FLOW.FAILED && paymentIntent ? (
                <div style={{ textAlign: 'center' }}>
                  <div role="alert" style={alertStyle('error')}>{error}</div>
                  <MovdButton onClick={() => finalizeBooking(paymentIntent)} loading={finalizing} style={{ width: '100%', marginTop: 16 }}>Retry booking confirmation</MovdButton>
                </div>
              ) : (
                <>
                  <h3 style={{ margin: '0 0 4px', fontSize: 20, fontWeight: 900, textAlign: 'center' }}>Secure your booking</h3>
                  <p style={{ ...mutedStyle, textAlign: 'center', marginBottom: 16 }}>Review the total below, then pay securely to book your mover.</p>
                  <EscrowStripePayment
                    price={Number(bookingData?.price ?? priceNumber)}
                    userEmail={contactEmail}
                    moverName={moverName}
                    bookingId={bookingId}
                    jobType="local"
                    premiumProtection={false}
                    onSuccess={finalizeBooking}
                  />
                </>
              )}
            </div>
          )}

          {error && flow !== FLOW.FAILED && <div role="alert" style={alertStyle('error')}>{error}</div>}
          {success && !showPayment && <div style={alertStyle('success')}>Booking successful! Proceed to payment below.</div>}
        </div>
      </div>
    </main>
  );
}
